/**
 * DeleteAgentConfirmDialog
 *
 * Pre-flight delete dialog. On open, queries the backend for non-terminal
 * task_states tied to this agent (namespace match + JSON-path filter on
 * inputData.agent_id). If hits exist, warns the user that deletion will
 * cancel those tasks. Otherwise shows the standard confirmation message.
 *
 * Wraps the shared ConfirmDialog so visual styling stays consistent.
 *
 * NOTE: finbot has no TaskManagement subdir, so the gql query is inlined
 * here rather than imported from a sibling module.
 */
import React, { useMemo } from 'react';
import { gql, useQuery } from '@apollo/client';
import { ConfirmDialog } from '../../Notification/ConfirmDialog';
import { Agent } from '../../../types';
import { TaskState } from '../../../types/task';
import { useAdminTranslation } from '../../../modules/i18n';

interface DeleteAgentConfirmDialogProps {
  isOpen: boolean;
  agent: Agent | null;
  onConfirm: () => void | Promise<void>;
  onCancel: () => void;
}

const NON_TERMINAL_STATUSES = ['PENDING', 'QUEUED', 'RUNNING', 'PAUSED', 'RETRYING'];

const GET_TASK_STATES_FOR_AGENT = gql`
  query GetTaskStatesForAgent($args: JSONObject!) {
    findManyTaskStates(args: $args) {
      id
      taskId
      taskType
      namespace
      status
      startedAt
      inputData
    }
  }
`;

const formatRunningFor = (startedAt: string | null | undefined): string => {
  if (!startedAt) return '';
  const startMs = new Date(startedAt).getTime();
  if (!Number.isFinite(startMs)) return '';
  const elapsedSec = Math.max(0, Math.floor((Date.now() - startMs) / 1000));
  const h = Math.floor(elapsedSec / 3600);
  const m = Math.floor((elapsedSec % 3600) / 60);
  const s = elapsedSec % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
};

export const DeleteAgentConfirmDialog: React.FC<DeleteAgentConfirmDialogProps> = ({ isOpen, agent, onConfirm, onCancel }) => {
  const { t } = useAdminTranslation();

  const namespace = agent?.knowledgeGraph?.namespace;

  const { data, loading } = useQuery(GET_TASK_STATES_FOR_AGENT, {
    variables: {
      args: {
        where: {
          namespace,
          status: { in: NON_TERMINAL_STATUSES },
          inputData: { path: ['agent_id'], equals: agent?.id }
        },
        orderBy: { startedAt: 'desc' },
        take: 25
      }
    },
    skip: !isOpen || !namespace || !agent?.id,
    fetchPolicy: 'network-only'
  });

  const inFlightTasks: TaskState[] = useMemo(() => data?.findManyTaskStates ?? [], [data]);

  const message = useMemo(() => {
    if (!agent) return '';
    if (loading) {
      return t('content.agents.deleteConfirm.checking');
    }
    if (inFlightTasks.length === 0) {
      return t('content.agents.deleteConfirm.message', { name: agent.name } as any);
    }
    const taskLines = inFlightTasks
      .map((task) => {
        const duration = formatRunningFor(task.startedAt);
        return duration
          ? `${task.taskType} (${task.status.toLowerCase()}, ${duration})`
          : `${task.taskType} (${task.status.toLowerCase()})`;
      })
      .join(', ');
    return t('content.agents.deleteConfirm.messageWithRunning', {
      name: agent.name,
      count: inFlightTasks.length,
      tasks: taskLines
    } as any);
  }, [agent, loading, inFlightTasks, t]);

  return (
    <ConfirmDialog
      isOpen={isOpen}
      title={t('content.agents.deleteConfirm.title')}
      message={message}
      confirmText={t('content.agents.deleteConfirm.confirmButton')}
      cancelText={t('content.agents.deleteConfirm.cancelButton')}
      confirmVariant='danger'
      onConfirm={onConfirm}
      onCancel={onCancel}
    />
  );
};
